import React, { createContext, useContext, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  type SortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { cn } from 'cn';

type SortableProps = {
  /** Stable item IDs in their current render order. */
  items: string[];
  /** Called with the dragged item's old and new index; persist with `arrayMove`. */
  onReorder: (oldIndex: number, newIndex: number) => void;
  /** Dnd-kit sorting strategy; defaults to a vertical list. */
  strategy?: SortingStrategy;
  children: React.ReactNode;
};

/**
 * Drag-and-drop sortable list built on dnd-kit. Wrap the list in `Sortable`,
 * each row in a `SortableItem` whose `id` appears in `items`, and (optionally)
 * put a `SortableItemHandle` inside the row to restrict dragging to a handle.
 * Supports pointer and keyboard (Space/Enter + arrows) interaction.
 */
const Sortable: React.FC<SortableProps> = ({
  items,
  onReorder,
  strategy = verticalListSortingStrategy,
  children,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(oldIndex, newIndex);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

type SortableItemContextValue = {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
};

const SortableItemContext = createContext<SortableItemContextValue | undefined>(
  undefined
);

const useSortableItemContext = (): SortableItemContextValue => {
  const context = useContext(SortableItemContext);
  if (context === undefined) {
    throw new Error(
      'useSortableItemContext must be used within a SortableItem'
    );
  }
  return context;
};

type SortableItemProps = React.ComponentProps<'div'> & {
  /** Must match one of the parent `Sortable`'s `items` IDs. */
  id: string;
};

/**
 * A sortable row. While dragged it carries a `data-dragging` attribute, so drag
 * styling can be expressed as `tw:data-dragging:*` classes.
 */
const SortableItem: React.FC<SortableItemProps> = ({
  id,
  style,
  children,
  ...props
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const contextValue = useMemo(
    () => ({ attributes, listeners }),
    [attributes, listeners]
  );

  return (
    <SortableItemContext.Provider value={contextValue}>
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(transform),
          transition,
          ...style,
        }}
        data-dragging={isDragging || undefined}
        {...props}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  );
};

/** Drag handle for the enclosing `SortableItem`; give it an `aria-label`. */
const SortableItemHandle: React.FC<React.ComponentProps<'span'>> = ({
  className,
  children,
  ...props
}) => {
  const { attributes, listeners } = useSortableItemContext();

  return (
    <span
      {...attributes}
      {...listeners}
      {...props}
      className={cn(
        'tw:cursor-grab tw:touch-none tw:active:cursor-grabbing',
        className
      )}
    >
      {children}
    </span>
  );
};

export { Sortable, SortableItem, SortableItemHandle, arrayMove };
